import { c as _c } from "react/compiler-runtime";
// useSmartVideo.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
type SmartVideoOptions = {
  loadRootMargin?: string;
  playThreshold?: number;
  warmFrame?: boolean;
  forcePreloadAutoOnLoad?: boolean;

  /**
   * Priorytet dla kluczowego wideo (np. pierwsze w gridzie).
   * eagerLoad: startuje ładowanie od razu (bez czekania na IO)
   * eagerPlay: próbuje odtwarzać od razu po shouldLoad
   * ignoreAvoidAutoplay: ignoruje Save-Data/2G dla tego jednego wideo (opcjonalne)
   */
  eagerLoad?: boolean;
  eagerPlay?: boolean;
  ignoreAvoidAutoplay?: boolean;

  /**
   * Retry autoplay (ważne dla iOS/Safari i przy dużym obciążeniu).
   * Domyślnie: szybkie próby w pierwszej sekundzie.
   */
  playRetryDelaysMs?: number[];
};
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
function shouldAvoidAutoplay(): boolean {
  if (typeof navigator === "undefined") return false;
  const anyNav = navigator as any;
  const conn = anyNav.connection || anyNav.mozConnection || anyNav.webkitConnection;
  const saveData = Boolean(conn?.saveData);
  const effectiveType: string | undefined = conn?.effectiveType;
  const slow = effectiveType === "slow-2g" || effectiveType === "2g";
  return saveData || slow;
}
async function safePlay(video: HTMLVideoElement): Promise<void> {
  video.muted = true;
  video.playsInline = true;
  try {
    await video.play();
  } catch {
    // ignore
  }
}
function safePause(video: HTMLVideoElement): void {
  try {
    video.pause();
  } catch {
    // ignore
  }
}
export function useSmartVideo(t0, t1) {
  const $ = _c(43);
  const isDisabled = t0 === undefined ? false : t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const options = t2;
  const {
    loadRootMargin: t3,
    playThreshold: t4,
    warmFrame: t5,
    forcePreloadAutoOnLoad: t6,
    eagerLoad: t7,
    eagerPlay: t8,
    ignoreAvoidAutoplay: t9,
    playRetryDelaysMs: t10
  } = options;
  const loadRootMargin = t3 === undefined ? "600px 0px 600px 0px" : t3;
  const playThreshold = t4 === undefined ? 0.1 : t4;
  const warmFrame = t5 === undefined ? true : t5;
  const forcePreloadAutoOnLoad = t6 === undefined ? true : t6;
  const eagerLoad = t7 === undefined ? false : t7;
  const eagerPlay = t8 === undefined ? false : t8;
  const ignoreAvoidAutoplay = t9 === undefined ? false : t9;
  let t11;
  if ($[2] !== t10) {
    t11 = t10 === undefined ? [0, 120, 280, 600, 1000] : t10;
    $[2] = t10;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const playRetryDelaysMs = t11;
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  let t12;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = prefersReducedMotion();
    $[4] = t12;
  } else {
    t12 = $[4];
  }
  const reduced = t12;
  let t13;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = shouldAvoidAutoplay();
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const avoidAutoplayBase = t13;
  const avoidAutoplay = ignoreAvoidAutoplay ? false : avoidAutoplayBase;
  const [shouldLoad, setShouldLoad] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);
  const warmedOnce = useRef(false);
  let t14;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[6] = t14;
  } else {
    t14 = $[6];
  }
  const retryTimers = useRef(t14);
  let t15;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      retryTimers.current.forEach(_temp);
      retryTimers.current = [];
    };
    $[7] = t15;
  } else {
    t15 = $[7];
  }
  const clearRetries = t15;
  let t16;
  if ($[8] !== playRetryDelaysMs) {
    t16 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      clearRetries();
      const tryOnce = () => {
        safePlay(video);
      };
      tryOnce();
      requestAnimationFrame(tryOnce);
      for (const d of playRetryDelaysMs) {
        const id_0 = window.setTimeout(() => {
          if (!video.paused) {
            return;
          }
          tryOnce();
        }, d);
        retryTimers.current.push(id_0);
      }
    };
    $[8] = playRetryDelaysMs;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const attemptPlay = t16;
  let t17;
  let t18;
  if ($[10] !== eagerLoad || $[11] !== isDisabled || $[12] !== loadRootMargin || $[13] !== playThreshold) {
    t17 = () => {
      if (isDisabled) {
        return;
      }
      if (eagerLoad) {
        setShouldLoad(true);
      }
      const el = containerRef.current;
      if (!el) {
        return;
      }
      let lastPlay = false;
      let loadObserver = null;
      if (!eagerLoad) {
        loadObserver = new IntersectionObserver(t19 => {
          const [entry] = t19;
          if (entry?.isIntersecting) {
            setShouldLoad(true);
            loadObserver?.disconnect();
          }
        }, {
          rootMargin: loadRootMargin
        });
        loadObserver.observe(el);
      }
      const playObserver = new IntersectionObserver(t20 => {
        const [entry_0] = t20;
        const next = Boolean(entry_0?.isIntersecting);
        if (next !== lastPlay) {
          lastPlay = next;
          setShouldPlay(next);
        }
      }, {
        threshold: playThreshold
      });
      playObserver.observe(el);
      return () => {
        loadObserver?.disconnect();
        playObserver.disconnect();
      };
    };
    t18 = [isDisabled, loadRootMargin, playThreshold, eagerLoad];
    $[10] = eagerLoad;
    $[11] = isDisabled;
    $[12] = loadRootMargin;
    $[13] = playThreshold;
    $[14] = t17;
    $[15] = t18;
  } else {
    t17 = $[14];
    t18 = $[15];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[16] !== forcePreloadAutoOnLoad || $[17] !== isDisabled || $[18] !== shouldLoad || $[19] !== warmFrame) {
    t19 = () => {
      const video_0 = videoRef.current;
      if (!video_0) {
        return;
      }
      if (isDisabled || reduced) {
        safePause(video_0);
        return;
      }
      if (!shouldLoad) {
        return;
      }
      if (forcePreloadAutoOnLoad && video_0.preload !== "auto") {
        video_0.preload = "auto";
      }
      try {
        video_0.load();
      } catch {}
      if (!warmFrame || warmedOnce.current) {
        return;
      }
      warmedOnce.current = true;
      const onMeta = () => {
        try {
          if (video_0.currentTime === 0) {
            video_0.currentTime = 0.001;
          }
        } catch {}
        video_0.removeEventListener("loadedmetadata", onMeta);
      };
      video_0.addEventListener("loadedmetadata", onMeta);
      return () => {
        video_0.removeEventListener("loadedmetadata", onMeta);
      };
    };
    t20 = [shouldLoad, isDisabled, reduced, warmFrame, forcePreloadAutoOnLoad];
    $[16] = forcePreloadAutoOnLoad;
    $[17] = isDisabled;
    $[18] = shouldLoad;
    $[19] = warmFrame;
    $[20] = t19;
    $[21] = t20;
  } else {
    t19 = $[20];
    t20 = $[21];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[22] !== attemptPlay || $[23] !== avoidAutoplay || $[24] !== eagerPlay || $[25] !== isDisabled || $[26] !== shouldLoad || $[27] !== shouldPlay) {
    t21 = () => {
      const video_1 = videoRef.current;
      if (!video_1) {
        return;
      }
      if (isDisabled || reduced) {
        clearRetries();
        safePause(video_1);
        return;
      }
      if (!shouldLoad) {
        return;
      }
      const inView = eagerPlay ? true : shouldPlay;
      const canPlay = inView && document.visibilityState === "visible" && !avoidAutoplay;
      if (canPlay) {
        attemptPlay();
      } else {
        clearRetries();
        safePause(video_1);
      }
    };
    t22 = [shouldPlay, shouldLoad, isDisabled, reduced, avoidAutoplay, eagerPlay, attemptPlay, clearRetries];
    $[22] = attemptPlay;
    $[23] = avoidAutoplay;
    $[24] = eagerPlay;
    $[25] = isDisabled;
    $[26] = shouldLoad;
    $[27] = shouldPlay;
    $[28] = t21;
    $[29] = t22;
  } else {
    t21 = $[28];
    t22 = $[29];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[30] !== attemptPlay || $[31] !== avoidAutoplay || $[32] !== eagerPlay || $[33] !== isDisabled || $[34] !== shouldLoad || $[35] !== shouldPlay) {
    t23 = () => {
      const video_2 = videoRef.current;
      if (!video_2) {
        return;
      }
      const onVis = () => {
        if (document.visibilityState !== "visible") {
          clearRetries();
          safePause(video_2);
          return;
        }
        const inView_0 = eagerPlay ? true : shouldPlay;
        if (!isDisabled && shouldLoad && inView_0 && !reduced && !avoidAutoplay) {
          attemptPlay();
        }
      };
      document.addEventListener("visibilitychange", onVis);
      return () => document.removeEventListener("visibilitychange", onVis);
    };
    t24 = [isDisabled, shouldLoad, shouldPlay, reduced, avoidAutoplay, eagerPlay, attemptPlay, clearRetries];
    $[30] = attemptPlay;
    $[31] = avoidAutoplay;
    $[32] = eagerPlay;
    $[33] = isDisabled;
    $[34] = shouldLoad;
    $[35] = shouldPlay;
    $[36] = t23;
    $[37] = t24;
  } else {
    t23 = $[36];
    t24 = $[37];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[38] !== isDisabled) {
    t25 = () => {
      if (!isDisabled) {
        return;
      }
      const video_3 = videoRef.current;
      if (video_3) {
        clearRetries();
        safePause(video_3);
      }
    };
    t26 = [isDisabled, clearRetries];
    $[38] = isDisabled;
    $[39] = t25;
    $[40] = t26;
  } else {
    t25 = $[39];
    t26 = $[40];
  }
  useEffect(t25, t26);
  let t27;
  if ($[41] !== shouldLoad) {
    t27 = {
      containerRef,
      videoRef,
      shouldLoad
    };
    $[41] = shouldLoad;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  return t27;
}
function _temp(id) {
  return window.clearTimeout(id);
}